import React from "react";
import { Check, X, Sparkles, ArrowRight, RotateCcw } from "lucide-react";

const SolutionPanel = ({ solution, rank, accent = "purple" }) => {
  const isPurple = accent === "purple";
  return (
    <div
      className={`relative overflow-hidden rounded-3xl p-6 sm:p-8 ${
        isPurple
          ? "bg-[#2a1f4a] text-white"
          : "bg-white border border-slate-200 text-[#2a1f4a]"
      }`}
      data-testid={`recommendation-${rank}`}
    >
      {isPurple && (
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-[#ecac1f]/20 blur-2xl pointer-events-none" />
      )}
      <div className="relative">
        <div className="flex items-center gap-2 mb-3">
          <span
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase ${
              isPurple
                ? "bg-[#ecac1f] text-[#2a1f4a]"
                : "bg-[#63499d]/10 text-[#63499d]"
            }`}
          >
            {isPurple ? <Sparkles className="w-3 h-3" /> : null}
            {rank === "primary" ? "Best Fit" : "Strong Alternate"}
          </span>
        </div>

        <h3
          className={`font-display text-2xl sm:text-3xl leading-tight mb-2 ${
            isPurple ? "text-white" : "text-[#2a1f4a]"
          }`}
          data-testid={`recommendation-${rank}-name`}
        >
          {solution.name}
        </h3>
        <p
          className={`text-sm sm:text-base leading-relaxed max-w-md mb-6 ${
            isPurple ? "text-white/80" : "text-slate-500"
          }`}
        >
          {solution.tagline}
        </p>

        {solution.reasons && solution.reasons.length > 0 && (
          <div className="mb-6">
            <div
              className={`text-[11px] font-bold uppercase tracking-[0.18em] mb-3 ${
                isPurple ? "text-[#ecac1f]" : "text-[#63499d]"
              }`}
            >
              Why it fits you
            </div>
            <ul className="space-y-2">
              {solution.reasons.map((r, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-2.5 text-sm ${
                    isPurple ? "text-white/90" : "text-[#404041]"
                  }`}
                >
                  <Check
                    className={`w-4 h-4 mt-0.5 shrink-0 ${
                      isPurple ? "text-[#ecac1f]" : "text-[#63499d]"
                    }`}
                  />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div
              className={`text-[11px] font-bold uppercase tracking-[0.18em] mb-2 ${
                isPurple ? "text-[#ecac1f]" : "text-[#63499d]"
              }`}
            >
              Pros
            </div>
            <ul className="space-y-1.5">
              {solution.pros.map((p, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-2 text-xs sm:text-sm ${
                    isPurple ? "text-white/85" : "text-[#404041]"
                  }`}
                >
                  <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#ecac1f]" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div
              className={`text-[11px] font-bold uppercase tracking-[0.18em] mb-2 ${
                isPurple ? "text-white/60" : "text-slate-400"
              }`}
            >
              Trade-offs
            </div>
            <ul className="space-y-1.5">
              {solution.cons.map((c, i) => (
                <li
                  key={i}
                  className={`flex items-start gap-2 text-xs sm:text-sm ${
                    isPurple ? "text-white/70" : "text-slate-500"
                  }`}
                >
                  <X
                    className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                      isPurple ? "text-white/50" : "text-slate-400"
                    }`}
                  />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export const RecommendationCard = ({ result, onGetQuote, onRestart }) => {
  if (!result) return null;
  const { primary, alternate, is_hybrid } = result;

  return (
    <section
      className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14 rc-fade-up"
      data-testid="recommendation-section"
    >
      <div className="mb-8 text-center">
        <div className="inline-flex items-center gap-2 mb-3">
          <span className="gold-bar" />
          <span className="text-[#63499d] text-xs font-bold tracking-[0.18em] uppercase">
            Your Match
          </span>
          <span className="gold-bar" />
        </div>
        <h2
          className="font-display text-3xl sm:text-4xl text-[#2a1f4a] leading-tight"
          data-testid="recommendation-headline"
        >
          {is_hybrid ? "A combined solution fits you best" : "Here's your best-fit solution"}
        </h2>
        <p className="text-sm sm:text-base text-slate-500 mt-3 max-w-2xl mx-auto">
          {is_hybrid
            ? "Based on your answers, pairing these two services will get you the biggest wins."
            : "Based on your answers, this is the Ricochet service that will deliver the biggest impact."}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
        <SolutionPanel solution={primary} rank="primary" accent="purple" />
        {is_hybrid ? (
          <SolutionPanel solution={alternate} rank="alternate" accent="white" />
        ) : (
          <div
            className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8"
            data-testid="recommendation-alternate-soft"
          >
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold tracking-[0.18em] uppercase bg-[#63499d]/10 text-[#63499d] mb-3">
              Also Worth Considering
            </div>
            <h3 className="font-display text-2xl text-[#2a1f4a] mb-2">
              {alternate.name}
            </h3>
            <p className="text-sm text-slate-500 mb-5 max-w-md">
              {alternate.tagline}
            </p>
            <ul className="space-y-1.5">
              {alternate.pros.slice(0, 3).map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm text-[#404041]"
                >
                  <Check className="w-4 h-4 mt-0.5 text-[#63499d] shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-10 bg-white border-2 border-dashed border-[#63499d]/30 rounded-2xl p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-block w-2 h-2 rounded-full bg-[#ecac1f] animate-pulse" />
            <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#63499d]">
              Next Step
            </span>
          </div>
          <div className="font-display text-lg sm:text-xl text-[#2a1f4a] leading-tight">
            Get a free quote tailored to your operation
          </div>
          <div className="text-sm text-slate-500 mt-1">
            We'll review your answers and send back a custom proposal — no obligation.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onRestart}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[#63499d] hover:text-[#4d3884] transition-colors"
            data-testid="recommendation-restart-button"
          >
            <RotateCcw className="w-4 h-4" />
            Retake
          </button>
          <button
            type="button"
            onClick={onGetQuote}
            className="btn-primary inline-flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-bold"
            data-testid="recommendation-quote-button"
          >
            Get a Free Quote
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default RecommendationCard;
